import { z } from "zod";
/**
 * Portal vault — read-only schemas for what magic-link-authenticated
 * customer users see when browsing shared documents.
 *
 * Distinct from `documents.ts` (the staff vault schemas) by design.
 * The staff schemas carry fields portal users must NOT see:
 *   - `visibleToRoles` (staff RBAC, not the portal user's concern)
 *   - `extractedSummary` / `extractedText` (OCR pipeline output;
 *     staff-only intelligence layer)
 *   - `uploadedBy` (staff email, leaks internal identity)
 *   - `templateFields` (signature template metadata)
 *
 * Canon: docs/design-canon/04-shared/ssot.md (Zod first; routes
 * import these and use zValidator).
 */
// ─── Folder ────────────────────────────────────────────────
/**
 * Portal folder — minimal projection of `vault_folders`.
 * Only folders with `sharedWithPortal = true` are ever returned.
 */
export const PortalVaultFolderSchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    // Folders nest; portal users see the same hierarchy, but can only
    // descend into shared folders. parentId of `null` = root-level.
    parentId: z.string().uuid().nullable(),
    sortOrder: z.number().int(),
});
export const PortalVaultFolderListResponseSchema = z.object({
    folders: z.array(PortalVaultFolderSchema),
});
// ─── Document ──────────────────────────────────────────────
/**
 * Portal document — minimal projection of `vault_documents` for
 * a customer viewer. No staff-side metadata (uploader, OCR, audit).
 */
export const PortalVaultDocumentSchema = z.object({
    id: z.string().uuid(),
    folderId: z.string().uuid(),
    title: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
    // Tags are useful operationally for filtering and don't leak
    // staff context (already chosen to be customer-shareable).
    tags: z.array(z.string()).default([]),
});
export const PortalVaultDocumentListResponseSchema = z.object({
    documents: z.array(PortalVaultDocumentSchema),
    total: z.number().int().nonnegative(),
});
// ─── Query params ──────────────────────────────────────────
export const PortalVaultDocumentListQuerySchema = z.object({
    // When omitted, returns documents at the root of all shared folders.
    // When set, returns documents in that specific folder — server
    // verifies the folder is shared before returning anything.
    folderId: z.string().uuid().optional(),
    // Bounded — portal viewers don't need 200-row pages.
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
// ─── Download ──────────────────────────────────────────────
/**
 * Response from `GET /api/portal/vault/documents/:id/download`.
 * Returns a short-lived signed URL. Portal user's browser follows
 * the URL directly to blob storage — the API never streams bytes.
 */
export const PortalVaultDownloadResponseSchema = z.object({
    url: z.string().url(),
    // ISO timestamp; the URL is invalid after this. Client uses to
    // refresh if a download fails with 403.
    expiresAt: z.string().datetime(),
    filename: z.string(),
});
//# sourceMappingURL=portal-vault.js.map